import { useMemo } from 'react';

// Types
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';

//hooks
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import {
    useGlobalInfo,
    TOGGLE_MENU_SELECTED_GROUP_OPEN,
    UPDATE_ACTIVE_SOCIAL_PROFILE_IN_GROUP
} from '@/utils/hooks/context/useGlobalInfo';

//components
import Icon from '@/components/base/Icon';
import { ListWrapper } from '@/components/composite/SocialProfiles/Components/ListWrapper';
import { AvatarMenu } from '@/components/composite/SocialProfiles/Components/AvatarMenu';
import { SocialProfilesMenuContent } from '@/components/composite/SocialProfiles/SocialProfileGroupMenu/Components/MenuContent/SocialProfiles';

type GroupMenu = {
    group: SpGroupType;
    initialSocialProfilePosition: number;
    setInitialSocialProfilePosition: (position: number) => void;
};

export function GroupMenu({
    group,
    initialSocialProfilePosition,
    setInitialSocialProfilePosition
}: GroupMenu) {
    const { dispatch, globalInfo } = useGlobalInfo();
    const { setActiveSocialProfile } = useActiveSocialProfile();
    const { spGroups } = useSPGroups({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const spGroupsStringified = JSON.stringify(spGroups);

    const activeGroup = useMemo(
        () => spGroups.find((currentGroup) => currentGroup.id === group.id),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [group.id, spGroupsStringified]
    );

    const activeSocialProfileGroup = useMemo(() => {
        if (globalInfo?.group?.activeSocialProfile?.id) {
            return activeGroup.social_profiles.find(
                (socialProfile) => globalInfo.group.activeSocialProfile.id === socialProfile.id
            );
        }

        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalInfo?.group?.activeSocialProfile?.id]);

    return (
        <div className="flex flex-col items-center space-y-6" data-testid="menu-group">
            <button
                className="flex h-5 w-5 items-center justify-center rounded-full bg-icoBlack/20 dark:bg-white/20"
                onClick={() => dispatch({ type: TOGGLE_MENU_SELECTED_GROUP_OPEN, payload: false })}
                data-testid="back-button">
                <Icon icon="chevron-left" className="pr-px text-xs text-white" />
            </button>
            <ListWrapper>
                <div className="flex flex-col items-center space-y-3.5 pb-0.5">
                    <button
                        data-testid="selected-group-button"
                        className="rounded-md"
                        onClick={() => {
                            if (globalInfo.group.activeSocialProfile) {
                                return dispatch({
                                    type: UPDATE_ACTIVE_SOCIAL_PROFILE_IN_GROUP,
                                    payload: null
                                });
                            }
                            return undefined;
                        }}>
                        <AvatarMenu
                            data={group}
                            variant="group"
                            selected={globalInfo.group.activeSocialProfile === null}
                            data-testid={
                                globalInfo.group.activeSocialProfile === null
                                    ? 'selected-group-active'
                                    : 'selected-group'
                            }
                        />
                    </button>
                    <span className="w-9 rounded-full border border-icoBlack/20 dark:border-white/20" />
                </div>
                <SocialProfilesMenuContent
                    variant="group"
                    initialSocialProfilePosition={initialSocialProfilePosition}
                    setInitialSocialProfilePosition={setInitialSocialProfilePosition}
                    itemsList={activeGroup?.social_profiles?.filter(
                        (socialProfile) => socialProfile.active
                    )}
                    setActiveItem={setActiveSocialProfile}
                    activeItem={activeSocialProfileGroup}
                />
            </ListWrapper>
        </div>
    );
}
